import { useEffect, useState } from 'react';
import { apiClient } from '@/services/api';
import { adminApiClient } from '@/services/adminApi';
import { useIsBootstrapMode, FormField, Skeleton } from '@connor-music/shared';
import type { AppConfig } from '@shared/types';
import { Settings, AlertCircle, CheckCircle, Loader2, Save } from 'lucide-react';

export default function AdminConfiguration() {
  const isBootstrap = useIsBootstrapMode();
  const api = isBootstrap ? adminApiClient : apiClient;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/50">
          <Settings className="h-5 w-5 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Portal Configuration
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Customize the portal branding, logos, and default values.
          </p>
        </div>
      </div>

      <BrandingTab api={api} />
    </div>
  );
}

// =============================================================================
// Branding Tab
// =============================================================================

function BrandingTab({ api }: { api: typeof apiClient | typeof adminApiClient }) {
  const [config, setConfig] = useState<AppConfig>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const data = await api.getConfig();
        setConfig(data);
      } catch (err) {
        console.error('Failed to load configuration:', err);
        setError('Failed to load configuration.');
      } finally {
        setIsLoading(false);
      }
    };
    loadConfig();
  }, [api]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsSaving(true);

    try {
      const updated = await api.updateConfig(config);
      setConfig(updated);
      setSuccessMessage('Configuration saved successfully.');
    } catch (err) {
      console.error('Failed to save configuration:', err);
      setError('Failed to save configuration.');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: keyof AppConfig, value: string | number | boolean | undefined) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const inputClasses =
    'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-primary-400 dark:focus:ring-primary-400';

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 space-y-4">
          <Skeleton className="h-5 w-20" />
          <div className="space-y-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-9 w-full rounded-md" />
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 space-y-4">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-3 w-64" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-9 w-full rounded-md" />
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end">
          <Skeleton className="h-9 w-36 rounded-md" />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Messages */}
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-400">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {successMessage && (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-green-50 dark:bg-green-900/20 p-4 text-sm text-green-700 dark:text-green-400">
          <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>{successMessage}</span>
        </div>
      )}

      <form onSubmit={handleSave}>
        {/* Site Banner */}
        <div className="rounded-lg border bg-white p-6 dark:border-gray-700 dark:bg-gray-800 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Site Banner
            </h3>
            <button
              type="button"
              onClick={() => updateField('banner_enabled', !config.banner_enabled)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                config.banner_enabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
              }`}
              role="switch"
              aria-checked={!!config.banner_enabled}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  config.banner_enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          {config.banner_enabled && (
            <div className="space-y-4">
              <FormField label="Banner Text" htmlFor="banner-text">
                <input
                  id="banner-text"
                  type="text"
                  value={config.banner_text ?? ''}
                  onChange={(e) => updateField('banner_text', e.target.value || undefined)}
                  className={inputClasses}
                  placeholder="e.g., TEST ENVIRONMENT - NOT FOR PRODUCTION DATA"
                />
              </FormField>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField label="Text Color" htmlFor="banner-text-color">
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={config.banner_text_color || '#1a1a00'}
                      onChange={(e) => updateField('banner_text_color', e.target.value)}
                      className="h-9 w-12 cursor-pointer rounded border border-gray-300 dark:border-gray-600 p-0.5"
                    />
                    <input
                      id="banner-text-color"
                      type="text"
                      value={config.banner_text_color ?? '#1a1a00'}
                      onChange={(e) => updateField('banner_text_color', e.target.value || undefined)}
                      className={inputClasses}
                      placeholder="#1a1a00"
                    />
                  </div>
                </FormField>
                <FormField label="Background Color" htmlFor="banner-bg-color">
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={config.banner_bg_color || '#facc15'}
                      onChange={(e) => updateField('banner_bg_color', e.target.value)}
                      className="h-9 w-12 cursor-pointer rounded border border-gray-300 dark:border-gray-600 p-0.5"
                    />
                    <input
                      id="banner-bg-color"
                      type="text"
                      value={config.banner_bg_color ?? '#facc15'}
                      onChange={(e) => updateField('banner_bg_color', e.target.value || undefined)}
                      className={inputClasses}
                      placeholder="#facc15"
                    />
                  </div>
                </FormField>
              </div>
              {/* Live Preview */}
              {config.banner_text && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    Preview
                  </p>
                  <div
                    className="rounded-md text-center py-1 px-4 text-sm font-semibold"
                    style={{
                      backgroundColor: config.banner_bg_color || '#facc15',
                      color: config.banner_text_color || '#1a1a00',
                    }}
                  >
                    {config.banner_text}
                  </div>
                </div>
              )}
            </div>
          )}
          {!config.banner_enabled && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Enable to show a banner across the top of both portals.
            </p>
          )}
        </div>

        {/* Portal Name */}
        <div className="rounded-lg border bg-white p-6 dark:border-gray-700 dark:bg-gray-800 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            General
          </h3>
          <div className="space-y-4">
            <FormField label="Portal Name" htmlFor="portal-name">
              <input
                id="portal-name"
                type="text"
                value={config.portal_name ?? ''}
                onChange={(e) => updateField('portal_name', e.target.value)}
                className={inputClasses}
                placeholder="Connor Music Audit"
              />
            </FormField>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Terms of Use URL" htmlFor="terms-url">
                <input
                  id="terms-url"
                  type="url"
                  value={config.terms_url ?? ''}
                  onChange={(e) => updateField('terms_url', e.target.value)}
                  className={inputClasses}
                  placeholder="https://example.com/terms"
                />
              </FormField>
              <FormField label="Privacy Policy URL" htmlFor="privacy-url">
                <input
                  id="privacy-url"
                  type="url"
                  value={config.privacy_url ?? ''}
                  onChange={(e) => updateField('privacy_url', e.target.value)}
                  className={inputClasses}
                  placeholder="https://example.com/privacy"
                />
              </FormField>
            </div>
          </div>
        </div>

        {/* Logo URLs */}
        <div className="rounded-lg border bg-white p-6 dark:border-gray-700 dark:bg-gray-800 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Logos
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Provide URLs for your logo assets. Light and dark variants are used based on the user's theme.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Logo (Light Theme)" htmlFor="logo-light-url">
              <input
                id="logo-light-url"
                type="url"
                value={config.logo_light_url ?? ''}
                onChange={(e) => updateField('logo_light_url', e.target.value)}
                className={inputClasses}
                placeholder="https://example.com/logo-light.svg"
              />
            </FormField>

            <FormField label="Logo (Dark Theme)" htmlFor="logo-dark-url">
              <input
                id="logo-dark-url"
                type="url"
                value={config.logo_dark_url ?? ''}
                onChange={(e) => updateField('logo_dark_url', e.target.value)}
                className={inputClasses}
                placeholder="https://example.com/logo-dark.svg"
              />
            </FormField>

            <FormField label="Logo Mark (Light Theme)" htmlFor="logo-mark-light-url">
              <input
                id="logo-mark-light-url"
                type="url"
                value={config.logo_mark_light_url ?? ''}
                onChange={(e) => updateField('logo_mark_light_url', e.target.value)}
                className={inputClasses}
                placeholder="https://example.com/mark-light.svg"
              />
            </FormField>

            <FormField label="Logo Mark (Dark Theme)" htmlFor="logo-mark-dark-url">
              <input
                id="logo-mark-dark-url"
                type="url"
                value={config.logo_mark_dark_url ?? ''}
                onChange={(e) => updateField('logo_mark_dark_url', e.target.value)}
                className={inputClasses}
                placeholder="https://example.com/mark-dark.svg"
              />
            </FormField>

            <FormField label="Logo Width (px)" htmlFor="logo-width">
              <input
                id="logo-width"
                type="number"
                min={40}
                max={400}
                value={config.logo_width ?? ''}
                onChange={(e) =>
                  updateField('logo_width', e.target.value ? parseInt(e.target.value, 10) : undefined)
                }
                className={inputClasses}
                placeholder="120"
              />
            </FormField>

            <FormField label="Login Logo Width (px)" htmlFor="login-logo-width">
              <input
                id="login-logo-width"
                type="number"
                min={40}
                max={600}
                value={config.login_logo_width ?? ''}
                onChange={(e) =>
                  updateField('login_logo_width', e.target.value ? parseInt(e.target.value, 10) : undefined)
                }
                className={inputClasses}
                placeholder="200"
              />
            </FormField>

            <FormField label="Favicon URL" htmlFor="favicon-url">
              <input
                id="favicon-url"
                type="url"
                value={config.favicon_url ?? ''}
                onChange={(e) => updateField('favicon_url', e.target.value)}
                className={inputClasses}
                placeholder="https://example.com/favicon.ico"
              />
            </FormField>
          </div>
        </div>

        {/* Preview */}
        {(config.logo_light_url || config.logo_dark_url || config.logo_mark_light_url || config.logo_mark_dark_url) && (
          <div className="rounded-lg border bg-white p-6 dark:border-gray-700 dark:bg-gray-800 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Preview
            </h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {config.logo_light_url && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    Logo (Light)
                  </p>
                  <div className="rounded-md bg-white border border-gray-200 p-4 dark:border-gray-600">
                    <img
                      src={config.logo_light_url}
                      alt="Logo light preview"
                      style={{ width: config.logo_width || 120 }}
                      className="h-auto"
                    />
                  </div>
                </div>
              )}
              {config.logo_dark_url && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    Logo (Dark)
                  </p>
                  <div className="rounded-md bg-gray-900 border border-gray-700 p-4">
                    <img
                      src={config.logo_dark_url}
                      alt="Logo dark preview"
                      style={{ width: config.logo_width || 120 }}
                      className="h-auto"
                    />
                  </div>
                </div>
              )}
              {config.logo_mark_light_url && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    Mark (Light)
                  </p>
                  <div className="rounded-md bg-white border border-gray-200 p-4 dark:border-gray-600">
                    <img src={config.logo_mark_light_url} alt="Mark light preview" className="h-8 w-8" />
                  </div>
                </div>
              )}
              {config.logo_mark_dark_url && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    Mark (Dark)
                  </p>
                  <div className="rounded-md bg-gray-900 border border-gray-700 p-4">
                    <img src={config.logo_mark_dark_url} alt="Mark dark preview" className="h-8 w-8" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Login Screen Preview */}
        {(config.logo_light_url || config.logo_dark_url) && (
          <div className="rounded-lg border bg-white p-6 dark:border-gray-700 dark:bg-gray-800 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Login Screen Preview
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Preview of how the logo appears on the employee and customer login pages.
            </p>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Employee Login Preview */}
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Employee Login
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* Light theme */}
                  {config.logo_light_url && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:border dark:border-gray-600">
                      {/* Card mockup */}
                      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="text-center space-y-2">
                          <div className="flex justify-center">
                            <img
                              src={config.logo_light_url}
                              alt="Employee login light preview"
                              style={{ width: Math.min(config.login_logo_width || 200, 140) }}
                              className="h-auto"
                            />
                          </div>
                          <p className="text-[10px] font-bold text-gray-900 truncate">
                            {config.portal_name || 'Connor Music Audit'}
                          </p>
                          <p className="text-[8px] text-gray-500">Employee Portal</p>
                          <div className="rounded bg-gray-50 px-2 py-1">
                            <p className="text-[7px] text-gray-500">Authorized employees only</p>
                          </div>
                          <div className="rounded bg-accent-600 px-2 py-1">
                            <span className="text-[8px] font-medium text-white">Sign in with Microsoft Entra</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 flex justify-center gap-2">
                        <span className="text-[7px] text-gray-400">Terms</span>
                        <span className="text-[7px] text-gray-300">&middot;</span>
                        <span className="text-[7px] text-gray-400">Privacy</span>
                      </div>
                    </div>
                  )}
                  {/* Dark theme */}
                  {config.logo_dark_url && (
                    <div className="rounded-lg bg-gray-900 p-4 border border-gray-700">
                      {/* Card mockup */}
                      <div className="rounded-lg border border-gray-700 bg-gray-800 p-4 shadow-sm">
                        <div className="text-center space-y-2">
                          <div className="flex justify-center">
                            <img
                              src={config.logo_dark_url}
                              alt="Employee login dark preview"
                              style={{ width: Math.min(config.login_logo_width || 200, 140) }}
                              className="h-auto"
                            />
                          </div>
                          <p className="text-[10px] font-bold text-white truncate">
                            {config.portal_name || 'Connor Music Audit'}
                          </p>
                          <p className="text-[8px] text-gray-400">Employee Portal</p>
                          <div className="rounded bg-gray-700/50 px-2 py-1">
                            <p className="text-[7px] text-gray-300">Authorized employees only</p>
                          </div>
                          <div className="rounded bg-accent-600 px-2 py-1">
                            <span className="text-[8px] font-medium text-white">Sign in with Microsoft Entra</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 flex justify-center gap-2">
                        <span className="text-[7px] text-gray-500">Terms</span>
                        <span className="text-[7px] text-gray-600">&middot;</span>
                        <span className="text-[7px] text-gray-500">Privacy</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Login Preview */}
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Customer Login
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* Light theme */}
                  {config.logo_light_url && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:border dark:border-gray-600">
                      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="text-center space-y-2">
                          <div className="flex justify-center">
                            <img
                              src={config.logo_light_url}
                              alt="Customer login light preview"
                              style={{ width: Math.min(config.login_logo_width || 200, 140) }}
                              className="h-auto"
                            />
                          </div>
                          <p className="text-[10px] font-bold text-gray-900 truncate">
                            {config.portal_name || 'Connor Music Audit'}
                          </p>
                          <p className="text-[8px] text-gray-500">Customer Portal</p>
                          <div className="my-2 border-t border-gray-200" />
                          <div className="rounded bg-gray-50 px-2 py-1">
                            <span className="text-[7px] text-gray-600">Authorized audit participants only.</span>
                          </div>
                          <div className="rounded border border-gray-300 bg-white px-2 py-1">
                            <span className="text-[8px] text-gray-400">you@company.com</span>
                          </div>
                          <div className="rounded bg-accent-600 px-2 py-1">
                            <span className="text-[8px] font-medium text-white">Send Login Code</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 flex justify-center gap-2">
                        <span className="text-[7px] text-gray-400">Terms</span>
                        <span className="text-[7px] text-gray-300">&middot;</span>
                        <span className="text-[7px] text-gray-400">Privacy</span>
                      </div>
                    </div>
                  )}
                  {/* Dark theme */}
                  {config.logo_dark_url && (
                    <div className="rounded-lg bg-gray-900 p-4 border border-gray-700">
                      <div className="rounded-lg border border-gray-700 bg-gray-800 p-4 shadow-sm">
                        <div className="text-center space-y-2">
                          <div className="flex justify-center">
                            <img
                              src={config.logo_dark_url}
                              alt="Customer login dark preview"
                              style={{ width: Math.min(config.login_logo_width || 200, 140) }}
                              className="h-auto"
                            />
                          </div>
                          <p className="text-[10px] font-bold text-white truncate">
                            {config.portal_name || 'Connor Music Audit'}
                          </p>
                          <p className="text-[8px] text-gray-400">Customer Portal</p>
                          <div className="my-2 border-t border-gray-700" />
                          <div className="rounded bg-gray-700/50 px-2 py-1">
                            <span className="text-[7px] text-gray-300">Authorized audit participants only.</span>
                          </div>
                          <div className="rounded border border-gray-600 bg-gray-700 px-2 py-1">
                            <span className="text-[8px] text-gray-500">you@company.com</span>
                          </div>
                          <div className="rounded bg-accent-600 px-2 py-1">
                            <span className="text-[8px] font-medium text-white">Send Login Code</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 flex justify-center gap-2">
                        <span className="text-[7px] text-gray-500">Terms</span>
                        <span className="text-[7px] text-gray-600">&middot;</span>
                        <span className="text-[7px] text-gray-500">Privacy</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Save */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-primary-500 dark:hover:bg-primary-600 dark:focus:ring-offset-gray-800"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {isSaving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </form>
    </div>
  );
}

